import { Box, Button, Divider, Fade, Slide, Tooltip, Typography, useTheme } from '@mui/material'
import { TransitionGroup } from 'react-transition-group'
import { useSearchParams } from 'react-router-dom'
import Confetti from 'react-confetti-boom'
import { observer } from 'mobx-react'
import React from 'react'

import celebratingSimba from '../../../assets/simba-ai/celebration.png'
import SubjectPracticeSessions from './views/SubjectPracticeSessions'
import thinkingSimba from '../../../assets/simba-ai/Thinking.png'
import historySimba from '../../../assets/simba-ai/history.png'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import { formatDate, Icons } from '../../../utils/utilities'
import sadSimba from '../../../assets/simba-ai/sad.png'
import AIWaitingScreen from '../AIWaitingScreen'
import PracticeModeHome from './views/PracticeModeHome'
import QuizQuestion from './views/QuizQuestion'
import TipsAndMessages from './TipsAndMessages'
import ReportIssue from './ReportIssue'
import CustomChip from '../../common/CustomChip'
import Markdown from '../../../shared/components/common/custom-markdown/Markdown'
import { CustomCodeBlockReadOnly } from '../../../shared/components/common/custom-markdown/CustomCodeBlock'

/**
 * Returns a formal feedback sentence based on student performance.
 * @param {number} scoredMarks - The marks obtained by the student.
 * @param {number} totalMarks - The maximum possible marks.
 * @returns {string} - A professional feedback sentence.
 */
const getFormalFeedback = ( scoredMarks, totalMarks ) => {
    // Safety check to avoid division by zero or negative results
    if ( !totalMarks || totalMarks <= 0 ) return "No assessment data available for this session."

    const percentage = ( scoredMarks / totalMarks ) * 100

    // Returning sentences based on 10% increments
    if ( percentage >= 91 ) return "You have reached the pinnacle of subject mastery and academic excellence for this session."
    if ( percentage >= 81 ) return "Your performance reflects a deep and thorough mastery of all topics covered in this module."
    if ( percentage >= 71 ) return "You are demonstrating a sophisticated and high-level command of the subject matter."
    if ( percentage >= 61 ) return "Your command of the curriculum is becoming comprehensive and well-rounded."
    if ( percentage >= 51 ) return "You have successfully captured the majority of the primary learning objectives for this session."
    if ( percentage >= 41 ) return "Aim for a deeper analysis in your next session to bridge the remaining conceptual gaps."
    if ( percentage >= 31 ) return "You are beginning to navigate the subject complexities with increasing effectiveness."
    if ( percentage >= 21 ) return "Consistent review and practice of these specific materials will yield more stable results."
    if ( percentage >= 11 ) return "Reviewing the fundamental module topics will help strengthen your basic grasp of the material."

    return "Please focus on the core concepts to establish an initial foundation in this module."
}

const enterFullScreen = async () => {
    try {
        const quizContainer = document.getElementById( "simba-quiz-container" )
        if ( !window.document.fullscreenElement ) {
            if ( quizContainer ) {
                if ( quizContainer.requestFullscreen ) {
                    await quizContainer.requestFullscreen()
                } else if ( quizContainer.webkitRequestFullscreen ) {
                    // Safari
                    await quizContainer.webkitRequestFullscreen()
                } else if ( quizContainer.msRequestFullscreen ) {
                    // IE/Edge
                    await quizContainer.msRequestFullscreen()
                }
            }
        } else {
            await window.document.exitFullscreen()
        }
    } catch ( err ) {
        console.log( "Full screen error" )
    }
}

const Renderer = observer( () => {

    const { layout, palette, border, shadows } = useTheme()
    const SimbaAIStore = useSimbaAIStore()

    const [searchParams, setSearchParams] = useSearchParams()
    const practiceQuiz = searchParams.get( 'practiceQuiz' ) || ""

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const backToHome = async () => {
        SimbaAIStore.cancelQuestionStream()
        SimbaAIStore.setView( "home", "right" )
        setSearchParamsValues( { practiceQuiz: "" } )
        try {
            await window.document.exitFullscreen()
        } catch ( err ) {

        }
    }


    const backToHistory = () => {
        SimbaAIStore.setView( "history", "right" )
        setSearchParamsValues( { practiceQuiz: "" } )
    }

    const continuePractice = () => {
        SimbaAIStore.resumePendingPracticeSession()
        SimbaAIStore.setView( "question" )
        enterFullScreen()
    }

    const continueSelectedAttempt = ( attemptDetails ) => {

        let qIndex = attemptDetails.user_answers.filter( c => Boolean( c ) ).length
        const questions = attemptDetails.questions

        if ( qIndex >= questions.length ) {
            qIndex = questions.length - 1
        }
        SimbaAIStore.setPracticeQuestions( questions )
        SimbaAIStore.setQuizState( {
            conversationId: attemptDetails._id,
            userAnswers: attemptDetails.user_answers,
            attemptDetails: attemptDetails,
            questionIndex: qIndex,
            practiceQuizDetails: {
                practice_session_id: attemptDetails.practice_session._id,
                conversationId: attemptDetails._id
            }
        } )
        SimbaAIStore.setView( "question" )
        enterFullScreen()

    }

    const retryForAttempt = async () => {

        if ( !practiceQuiz ) return

        SimbaAIStore.setView( "loading" )
        const { success, data } = await SimbaAIStore.fetchPracticeSessionDetails( practiceQuiz )

        if ( success ) {
            if ( data.incomplete_session_exists ) {
                SimbaAIStore.setView( "continue-pending", "left", false )
            } else {
                if ( data.practiced ) {
                    SimbaAIStore.setView( "start-again", "left", false )
                } else {
                    const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceQuiz )
                    if ( success ) {
                        SimbaAIStore.setView( "question", "left", false )
                        enterFullScreen()
                    }
                }
            }
        }
    }

    const startPractice = async () => {
        enterFullScreen()
        setSearchParamsValues( { practiceQuiz: SimbaAIStore.getQuizState.practiceQuizDetails.practice_session_id } )
        SimbaAIStore.setView( "loading" )
        // Resolves on the first streamed question; the store switches to the error view itself on failure
        const success = await SimbaAIStore.fetchPracticeSessionQuestions( SimbaAIStore.getQuizState.practiceQuizDetails.practice_session_id )
        if ( success ) {
            SimbaAIStore.setView( "question", "left", false )
        }
    }


    switch ( SimbaAIStore.getView.current ) {
        case "loading":
            return (
                <AIWaitingScreen
                    sx={{ overflow: "hidden" }}
                    color='greyed'
                >
                    <TipsAndMessages variant='fade' />
                </AIWaitingScreen>
            )
        case "practice-session-details-fetch-error":
            return (
                <Box padding="20px">
                    <Box position="relative" border={`2px dashed ${palette.borderColor}`} borderRadius="20px" flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" gap="10px" justifyContent="center" >
                        <Box position="absolute" height="60%" maxHeight="400px" bgcolor={palette.contentBg} borderRadius="50%" sx={{ aspectRatio: "1 / 1", zIndex: 1 }} />
                        <Box position="relative" sx={{ zIndex: 10 }} component="img" src={sadSimba} height="180px" alt="No practice sessions" />
                        <Typography position="relative" sx={{ zIndex: 10 }} textAlign="center" variant="subtitle2" component="p" >{SimbaAIStore.getErrors.startingPractice || "Something went wrong while trying to generate your response"}</Typography>
                        <Box display="flex" gap={layout.cardsGap} alignItems="center" flexWrap="wrap">
                            <Button className='animation-wave' onClick={retryForAttempt} sx={{ borderBottom: `4px solid ${palette.secondary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Replay} variant='contained' disableElevation color='secondary' >Retry</Button>
                            <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Logout} variant='contained' disableElevation color='error' >Exit</Button>
                        </Box>
                    </Box>
                </Box>
            )

        case "continue-pending":
            return <Box padding={layout.pagePadding} display="flex" flexGrow={1} flexDirection={{ xs: "column", sm: "row" }} justifyContent="center" alignItems="center" gap="20px">
                <Box component="img" width="25vw" src={thinkingSimba} />
                <Box>

                    <Box width="fit-content" margin={{ xs: "0 auto 20px auto", sm: "20px 0 20px 0" }} alignItems={{ xs: "center", sm: "flex-start" }} borderRadius="10px" border={border[1]} bgcolor={palette.contentBg} padding="10px" display="flex" flexDirection="column">
                        <Typography fontWeight={600} textAlign={{ xs: "center", sm: "left" }} variant='subtitle1'>{SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.title}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" noWrap alignItems="center" gap="5px" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'  > {Icons.default.Sort2} {`Difficulty level: ${SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.complexity}`}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" gap="5px" alignItems="center" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'> {Icons.default.User} {SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.creator_info?.name}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" gap="5px" alignItems="center" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'> {Icons.default.Subjects} {SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.subject?.subject_name} - {Object.keys( SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.modules_and_topics || {} ).at( 0 )}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" gap="5px" alignItems="center" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'> {Icons.default.Category} {SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.modules_and_topics[Object.keys( SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.modules_and_topics || {} ).at( 0 )]?.topics?.join( ", " )}</Typography>
                    </Box>

                    <Typography textAlign={{ xs: "center", sm: "left" }} fontSize="26px" fontWeight={800}>There exists an incomplete attempt for this practice session! Would you like to continue from where you left off?</Typography>
                    <Box flexWrap="wrap" justifyContent={{ xs: "center", sm: "left" }} flexGrow={{ xs: "1", sm: "initial" }} marginTop="20px" display="flex" gap={layout.cardsGap} alignItems="center" >
                        <Button onClick={continuePractice} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} className='animation-wave' endIcon={Icons.default.ArrowRight} variant='outlined' color='primary' >Yes, Lets continue!</Button>
                        <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='outlined' color='error' >No, Lets go back to home.</Button>
                    </Box>
                </Box>
            </Box>

        case "start-again":
            return <Box padding={layout.pagePadding} display="flex" flexGrow={1} flexDirection={{ xs: "column", sm: "row" }} justifyContent="center" alignItems="center" gap="20px">
                <Box component="img" width="25vw" src={thinkingSimba} />
                <Box>

                    <Box width="fit-content" margin={{ xs: "0 auto 20px auto", sm: "20px 0 20px 0" }} alignItems={{ xs: "center", sm: "flex-start" }} borderRadius="10px" border={border[1]} bgcolor={palette.contentBg} padding="10px" display="flex" flexDirection="column">
                        <Typography fontWeight={600} textAlign={{ xs: "center", sm: "left" }} variant='subtitle1'>{SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.title}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" noWrap alignItems="center" gap="5px" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'> {Icons.default.Sort2} {`Difficulty level: ${SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.complexity}`}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" gap="5px" alignItems="center" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'> {Icons.default.User} {SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.creator_info?.name}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" gap="5px" alignItems="center" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'> {Icons.default.Subjects} {SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.subject?.subject_name} - {Object.keys( SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.modules_and_topics || {} ).at( 0 )}</Typography>
                        <Typography color='textSecondary' lineHeight={1.2} display="flex" gap="5px" alignItems="center" textAlign={{ xs: "center", sm: "left" }} fontSize="12px" variant='subtitle2'> {Icons.default.Category} {SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.modules_and_topics[Object.keys( SimbaAIStore.getQuizState.attemptDetails?.practice_session_details?.modules_and_topics || {} ).at( 0 )]?.topics?.join( ", " )}</Typography>
                    </Box>

                    <Typography textAlign={{ xs: "center", sm: "left" }} fontSize="26px" fontWeight={800}>You have already practiced these topics! Would you like to practice again?</Typography>
                    <Box flexWrap="wrap" justifyContent={{ xs: "center", sm: "left" }} flexGrow={{ xs: "1", sm: "initial" }} marginTop="20px" display="flex" gap={layout.cardsGap} alignItems="center" >
                        <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='outlined' disableElevation color='error' >Back to home</Button>
                        <Button onClick={() => SimbaAIStore.setView( "answers" )} sx={{ borderBottom: `4px solid ${palette.secondary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Checklist} variant='outlined' disableElevation color='secondary' >View latest results</Button>
                        <Button onClick={startPractice} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} className='animation-wave' endIcon={Icons.default.ArrowForward} variant='contained' disableElevation color='primary' >Start again!</Button>
                    </Box>
                </Box>
            </Box>

        case "question":
            return <QuizQuestion enterFullScreen={enterFullScreen} />

        case "result":
            return ( <Box display="flex" padding="20px" flexGrow={1} flexDirection={{ xs: "column", sm: "row" }} justifyContent="center" alignItems="center" gap="20px">

                <Confetti mode="boom" particleCount={150} />

                <Box component="img" width="25vw" src={( SimbaAIStore.getQuizState.attemptDetails.score / SimbaAIStore.getPracticeSessionQuestions.length < 0.5 ) ? sadSimba : celebratingSimba} />
                <Box>
                    <Typography textAlign={{ xs: "center", sm: "left" }} component="p" fontSize="18px" fontWeight={800}>Great! Quiz Completed</Typography>
                    <Typography textAlign={{ xs: "center", sm: "left" }} component="p" fontSize="32px" fontWeight={800}>You got {SimbaAIStore.getQuizState.attemptDetails.score} out of {SimbaAIStore.getPracticeSessionQuestions.length} questions correct.</Typography>
                    <Typography textAlign={{ xs: "center", sm: "left" }} color='textSecondary' component="p" variant='subtitle2'>{getFormalFeedback( SimbaAIStore.getQuizState.attemptDetails.score, SimbaAIStore.getPracticeSessionQuestions.length )}</Typography>
                    <Box flexWrap="wrap" justifyContent={{ xs: "center", sm: "left" }} flexGrow={{ xs: "1", sm: "initial" }} marginTop="20px" display="flex" gap={layout.cardsGap} alignItems="center" >
                        <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px", flexGrow: { sm: "initial", xs: 1 } }} startIcon={Icons.default.ArrowBack} variant='contained' disableElevation color='error' >Back to home</Button>
                        <Button onClick={() => SimbaAIStore.setView( "answers" )} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px", flexGrow: { sm: "initial", xs: 1 } }} className='animation-wave' endIcon={Icons.default.ArrowRight} variant='contained' disableElevation color='primary' >View answers with detailed explaination</Button>
                    </Box>
                </Box>
            </Box> )

        case "history":
            return ( <Box minHeight={0} overflow="auto" display="flex" padding={layout.pagePadding} flexGrow={1} flexDirection="column" justifyContent="flex-start" alignItems="flex-start" gap={layout.sectionsGap}>
                <Box flexShrink={0} width="100%" padding={layout.pagePadding} borderRadius="10px" gap="20px" alignItems="center" display="flex" sx={{
                    background: palette.primary.light,
                    borderBottom: `4px solid ${palette.primary.light + "99"}`,
                    borderLeft: `4px solid ${palette.primary.light + "99"}`,
                }} >
                    <Box component="img" height="100px" src={historySimba} />
                    <Box>
                        <Typography lineHeight={1} color={palette.common.white} fontSize="28px" fontWeight={800}>Practice History.</Typography>
                        <Typography fontSize="12px" color={palette.common.white} fontWeight={500}>You have completed {SimbaAIStore.getPracticeHistory.length} practice sessions.</Typography>

                        <Box flexWrap="wrap" flexGrow={{ xs: "1", sm: "initial" }} marginTop="20px" display="flex" gap={layout.cardsGap} alignItems="center" >
                            <Button className="animation-wave" onClick={backToHome} sx={{ color: palette.common.white, border: `1px solid ${palette.common.white}`, borderBottom: `4px solid ${palette.common.white}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} disableElevation  >Back to home</Button>
                        </Box>
                    </Box>
                </Box>

                {SimbaAIStore.getPracticeHistory.length > 0 && <Box width="100%" display="grid"
                    gridTemplateColumns={{
                        md: "repeat(4,1fr)",
                        sm: "repeat(2,1fr)",
                        sx: "repeat(1,1fr)",
                    }}
                    gap="10px">
                    {SimbaAIStore.getPracticeHistory.map( ph => {
                        const topics = Object.values( ph.practice_session.modules_and_topics ).map( mod => mod.topics ).flat()

                        return (
                            <Box
                                flexShrink={0}
                                onClick={ph.status === "completed" ? () => SimbaAIStore.setActiveAttemptDetails( ph ) : undefined}
                                overflow="hidden"
                                key={ph._id}
                                display="flex"
                                flexDirection="column"
                                borderRadius="10px"
                                boxShadow={shadows[1]}
                                border={border[1]}
                                borderBottom={`4px solid ${palette.borderColor}`}
                                position="relative"
                                bgcolor={palette.contentBg}
                                sx={{
                                    cursor: ph.status !== "completed" ? "not-allowed" : "pointer",
                                    transition: "200ms",
                                    ":hover": ph.status !== "completed" ? {} : {
                                        borderColor: palette.primary.light,
                                    }
                                }}
                            >
                                <Box sx={{ opacity: ph.status !== "completed" ? 0.7 : 1, }} padding="10px" display="flex" alignItems="flex-start" gap="20px">
                                    <Box display="flex" flexDirection="column">
                                        {/* <Box display="flex" fontSize="20px" justifyContent="center" width="40px" sx={{ aspectRatio: "1 / 1" }} borderRadius="50%" bgcolor={palette.primary.dark} color={palette.primary.contrastText} alignItems="center">{Icons.default.QuizIcon}</Box> */}
                                        <Box borderBottom={`4px solid ${palette.primary.light + "99"}`} borderLeft={`4px solid ${palette.primary.light + "99"}`} padding="10px" borderRadius="20px" bgcolor={palette.primary.dark} display="flex" alignItems={"center"} flexDirection="column">
                                            <Typography color={palette.primary.contrastText} fontWeight="800" fontSize="18px" > {ph.status !== "completed" ? "?" : ph.score}</Typography>
                                            <Box minWidth="20px" width="100%" component="span" height="2px" bgcolor={palette.primary.contrastText} sx={{ opacity: 0.7 }} />
                                            <Typography color={palette.primary.contrastText} fontWeight="800" fontSize="18px" >{ph?.questions?.length}</Typography>
                                        </Box>
                                    </Box>
                                    <Box display="flex" overflow="hidden" width="100%" flexDirection="column" alignItems="flex-start">
                                        <Typography color='textSecondary' fontSize="12px">{formatDate( ph.attempted_at, { format: "DD-MM-YYYY hh:mm A" } )}</Typography>
                                        <Tooltip disableInteractive title={ph.practice_session.title}>
                                            <Typography fontSize="18px" width="100%" overflow="hidden" noWrap fontWeight={600}>{ph.practice_session.title}</Typography>
                                        </Tooltip>
                                        <Typography display="block" marginTop="-3px" fontSize="12px" component="em">{ph.practice_session.subject?.subject_name}</Typography>
                                        <Box minWidth={0} marginTop="10px" width="100%" display="flex" gap="10px" flexWrap="wrap" alignItems="center">
                                            {topics.slice( 0, 1 ).map( topic => (
                                                <Typography title={topic} noWrap display="block" minWidth={0} variant='subtitle2' key={topic} fontSize="12px" borderRadius="5px" border={`1px solid ${palette.text.primary}`} padding="2px 5px" >{topic}</Typography>
                                            ) )}
                                            {topics.length > 1 && <Typography fontSize="12px" borderRadius="5px" color='textPrimary' border={`1px solid ${palette.text.primary}`} bgcolor={palette.contentBg} padding="2px 5px" >+{topics.length - 1} More</Typography>}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box padding="5px 10px" flexGrow={1} width="100%" justifyContent="space-between" display="flex" alignItems="center" borderTop={border[1]}>
                                    {ph.status === "completed" && <Typography display="flex" fontSize="20px" gap="10px" color='success' borderRadius="50%" alignItems="center">{Icons.default.CheckCircleIcon} <Typography variant="subtitle2" color="inherit">Completed </Typography></Typography>}
                                    {ph.status !== "completed" && <Typography display="flex" fontSize="20px" gap="10px" color='greyed' borderRadius="50%" alignItems="center">{Icons.default.Pending} <Typography variant="subtitle2" color="inherit">Pending completion </Typography></Typography>}
                                    {ph.status !== "completed" && <Button onClick={() => continueSelectedAttempt( ph )} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} variant='contained' disableElevation endIcon={Icons.default.ArrowForward} size='small' color='primary'>Open</Button>}
                                </Box>
                            </Box>
                        )
                    } )}
                </Box>}

                {SimbaAIStore.getPracticeHistory.length === 0 && <Box gap="20px" bgcolor={palette.contentBg} display="flex" flexDirection="column" alignItems="center" justifyContent="center" borderRadius="10px" overflow="auto" border={border[1]} padding="20px" width="100%">
                    <Typography>You have not practiced anything yet!</Typography>
                    <Button onClick={backToHome} variant='outlined' color='error' sx={{ borderBottom: `4px solid ${palette.error.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} >Back to home</Button>
                </Box>}

            </Box> )

        case "answers":
            return ( <Box minHeight={0} display="flex" padding={layout.pagePadding} flexGrow={1} flexDirection="column" justifyContent="flex-start" alignItems="flex-start" gap={layout.cardsGap}>
                <Box flexShrink={0} width="100%" padding={layout.pagePadding} borderRadius="10px" gap={layout.cardsGap} alignItems="center" display="flex" sx={{
                    background: palette.primary.light,
                    borderBottom: `4px solid ${palette.primary.light + "55"}`,
                    borderLeft: `4px solid ${palette.primary.light + "55"}`,
                }} >
                    <Box component="img" height="120px" src={( SimbaAIStore.getQuizState.attemptDetails.score / SimbaAIStore.getQuizState.attemptDetails.questions.length < 0.5 ) ? sadSimba : celebratingSimba} />
                    <Box>
                        <Typography color={palette.common.white} fontSize="24px" fontWeight={600}>You got {SimbaAIStore.getQuizState.attemptDetails.score} out of {SimbaAIStore.getQuizState.attemptDetails.questions.length} questions correct.</Typography>
                        <Typography color={palette.common.white} fontSize="12px" fontWeight={500}>Check the answers below!</Typography>

                        <Box flexWrap="wrap" flexGrow={{ xs: "1", sm: "initial" }} marginTop="20px" display="flex" gap={layout.cardsGap} alignItems="center" >
                            {SimbaAIStore.getView.previous === "history" && <Button size='small' onClick={backToHistory} sx={{ color: palette.primary.main, bgcolor: palette.common.white, border: `1px solid ${palette.grey[500]}`, borderBottom: `4px solid ${palette.grey[500]}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='contained' disableElevation color='error' >Go Back</Button>}
                            {SimbaAIStore.getView.previous !== "history" && <Button size='small' onClick={backToHome} sx={{ color: palette.primary.main, bgcolor: palette.common.white, border: `1px solid ${palette.grey[500]}`, borderBottom: `4px solid ${palette.grey[500]}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='contained' disableElevation color='error' >Back to home</Button>}
                        </Box>
                    </Box>
                </Box>


                <Box flexGrow={1} width="100%" className="custom-scrollbar" display="flex" minHeight={0} flexDirection="column" justifyContent="flex-start" alignItems="flex-start" overflow="auto" gap={layout.cardsGap}>

                    <Box width="100%" bgcolor={palette.contentBg} borderRadius="10px" border={border[1]}>
                        <Typography padding="5px 10px" borderBottom={border[1]}>Feedback</Typography>
                        <Typography padding="10px" color='textSecondary' component="p" variant='subtitle2'>{getFormalFeedback( SimbaAIStore.getQuizState.attemptDetails.score, SimbaAIStore.getQuizState.attemptDetails?.questions?.length )}</Typography>
                    </Box>

                    <Box flexGrow={1} width="100%" display="flex" minHeight={0} flexDirection="column" justifyContent="flex-start" alignItems="flex-start" overflow="auto" gap="30px">
                        {SimbaAIStore.getQuizState.attemptDetails.questions.map( ( question, qIndex ) => {

                            let isCorrect, provided
                            const correctAnswers = new Set( question.correct_answers )

                            if ( question.correct_answers?.length > 1 ) {
                                provided = new Set( SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex] )
                            } else {
                                provided = new Set( [SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex]] )
                            }

                            const difference = provided.symmetricDifference( correctAnswers )
                            isCorrect = difference.size === 0

                            const issueReoprtDetails = {
                                ...question,
                                selected_answer: SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex],
                            }
                            return (
                                <Box width="100%" flexShrink={0} key={qIndex} flexGrow={1} display="flex" gap="10px" flexDirection="column">
                                    {qIndex !== 0 && <Divider />}
                                    <Box >
                                        <Typography color='textSecondary' fontWeight={600} fontSize="12px" gutterBottom>Question {qIndex + 1}</Typography>
                                        <Typography component="div">
                                            <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: "14px", marginBlockStart: 0, wordBreak: 'break-word', overflowWrap: 'anywhere', "& ul": { marginBlockStart: 0 }, "& ol": { marginBlockStart: 0 } }} >
                                                {`${question.question}`}
                                            </Markdown>
                                        </Typography>
                                        <ReportIssue key={qIndex} questionIndex={qIndex} attemptDetails={SimbaAIStore.getQuizState.attemptDetails} questionDetails={issueReoprtDetails} />
                                    </Box>
                                    <Box display="flex" flexDirection="column" gap="10px" borderRadius={layout.borderRadius}>

                                        {/* <Typography variant='subtitle2' display="flex" alignItems="center" gap="5px" color={isCorrect ? "success" : "error"} component="p"> <Typography component="span" color={isCorrect ? "success" : "error"} display="flex" alignItems="center">{isCorrect ? Icons.default.CheckCircleIcon : Icons.default.CancelIcon}</Typography>{isCorrect ? "Correct" : "Incorrect"}</Typography> */}
                                        <CustomChip startIcon={isCorrect ? Icons.default.CheckCircleIcon : Icons.default.CancelIcon} color={isCorrect ? "success" : "error"} value={isCorrect ? "Correct" : "Incorrect"} />

                                        <Box borderRadius={layout.borderRadius} display="flex" flexDirection="column" gap="5px">
                                            {question.answers.map( ( option, index ) => {
                                                const selected = SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex] === option || ( Array.isArray( SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex] ) && SimbaAIStore.getQuizState.attemptDetails.user_answers[qIndex]?.includes( option ) )
                                                const inCorrectAnswersList = question.correct_answers.includes( option )
                                                return (
                                                    <Box borderRadius={layout.borderRadius} bgcolor={selected ? inCorrectAnswersList ? palette.success.dark + 33 : palette.error.dark + 22 : "none"} border={border[1]} display="flex" paddingX="10px" alignItems="flex-start">
                                                        <Typography marginTop="10px" sx={{ color: "GrayText" }} fontSize="20px">{selected ? Icons.default.CheckboxChecked : Icons.default.Checkbox}</Typography>
                                                        <Box minWidth={0} flexGrow={1} padding="10px">
                                                            <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: "14px", fontWeight: inCorrectAnswersList ? 500 : 400, marginBlockStart: 0, wordBreak: 'break-word', overflowWrap: 'anywhere', "& p": { marginBottom: "0 !important" }, "& ul": { marginBlockStart: 0 }, "& ol": { marginBlockStart: 0 } }} >
                                                                {option}
                                                            </Markdown>
                                                        </Box>
                                                        {inCorrectAnswersList && <Typography marginTop="10px" color='success' display="flex" alignItems="center" fontSize="20px" component="span">{Icons.default.CheckCircleIconOutlined}</Typography>}
                                                        {!inCorrectAnswersList && selected && <Typography marginTop="10px" color='error' display="flex" alignItems="center" fontSize="20px" component="span">{Icons.default.CancelIcon}</Typography>}
                                                    </Box>
                                                )
                                            } )}
                                        </Box>
                                        <Box padding="10px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} border={border[1]} borderLeft={`4px solid ${palette.primary.main}`} >
                                            <Typography fontSize="14px" gap="10px" marginBottom="10px" fontWeight={600} display="flex" alignItems="center" color='primary'> {Icons.default.AskAI} SIMBA AI EXPLAINS</Typography>
                                            <Markdown components={{ code: CustomCodeBlockReadOnly }} styles={{ fontSize: "14px", marginBlockStart: 0, wordBreak: 'break-word', overflowWrap: 'anywhere', "& p": { marginBottom: "0 !important" }, "& ul": { marginBlockStart: 0 }, "& ol": { marginBlockStart: 0 } }} >
                                                {question.explanation}
                                            </Markdown>
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        } )}
                    </Box>
                </Box>
            </Box > )

        case "subject-practice-sessions":
            return <SubjectPracticeSessions enterFullScreen={enterFullScreen} />

        case "home":
            return <PracticeModeHome enterFullScreen={enterFullScreen} />

        default:
            return null
    }
} )


const PracticeModeViewRenderer = observer( ( { closeAiMentorDialog } ) => {

    const SimbaAIStore = useSimbaAIStore()

    return (
        <Box className="slim-custom-scrollbar" flexGrow={1} display="flex" flexDirection="column" overflow="auto" position="relative" gap="10px" >

            <TransitionGroup style={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <Fade
                    key={SimbaAIStore.getView.current}
                    timeout={{ enter: 100, exit: 0 }}
                >
                    <Slide
                        in
                        direction={SimbaAIStore.getView.slideDirection || "left"}
                        timeout={{ enter: 350, exit: 0 }}
                    >
                        <Box minHeight={0} flexGrow={1} overflow="auto" display="flex" flexDirection="column">
                            <Renderer closeAiMentorDialog={closeAiMentorDialog} />
                        </Box>
                    </Slide>
                </Fade>
            </TransitionGroup>
        </Box>
    )

} )

export default PracticeModeViewRenderer